/**
 * Minimal, strict CBOR (RFC 8949) decoder — enough for COSE_Key structures in WebAuthn.
 * Supports definite-length unsigned/negative ints, byte/text strings, arrays, maps, and the
 * simple values false/true/null. Rejects indefinite lengths, tags, floats and integers beyond
 * Number.MAX_SAFE_INTEGER.
 */
export class CborError extends Error {
    name = 'CborError';
}
const MAX_DEPTH = 16;
export function decodeCbor(data) {
    const { value, offset } = decodeItem(data, 0, 0);
    if (offset !== data.length)
        throw new CborError('trailing bytes');
    return value;
}
/** Decode the first item and report how many bytes it used (authenticatorData embeds COSE keys). */
export function decodeCborPrefix(data) {
    const { value, offset } = decodeItem(data, 0, 0);
    return { value, length: offset };
}
function readUint(data, offset, size) {
    if (offset + size > data.length)
        throw new CborError('unexpected end of input');
    let n = 0;
    for (let i = 0; i < size; i++)
        n = n * 256 + data[offset + i];
    if (!Number.isSafeInteger(n))
        throw new CborError('integer too large');
    return n;
}
function decodeItem(data, start, depth) {
    if (depth > MAX_DEPTH)
        throw new CborError('nesting too deep');
    if (start >= data.length)
        throw new CborError('unexpected end of input');
    const initial = data[start];
    const major = initial >> 5;
    const info = initial & 0x1f;
    let offset = start + 1;
    if (major === 7) {
        if (info === 20)
            return { value: false, offset };
        if (info === 21)
            return { value: true, offset };
        if (info === 22)
            return { value: null, offset };
        throw new CborError(`unsupported simple/float value ${info}`);
    }
    let arg;
    if (info < 24)
        arg = info;
    else if (info === 24) {
        arg = readUint(data, offset, 1);
        offset += 1;
    }
    else if (info === 25) {
        arg = readUint(data, offset, 2);
        offset += 2;
    }
    else if (info === 26) {
        arg = readUint(data, offset, 4);
        offset += 4;
    }
    else if (info === 27) {
        arg = readUint(data, offset, 8);
        offset += 8;
    }
    else
        throw new CborError('indefinite lengths are not supported');
    switch (major) {
        case 0:
            return { value: arg, offset };
        case 1:
            return { value: -1 - arg, offset };
        case 2:
        case 3: {
            if (offset + arg > data.length)
                throw new CborError('unexpected end of input');
            const slice = data.slice(offset, offset + arg);
            offset += arg;
            if (major === 2)
                return { value: slice, offset };
            return { value: new TextDecoder('utf-8', { fatal: true }).decode(slice), offset };
        }
        case 4: {
            const arr = [];
            for (let i = 0; i < arg; i++) {
                const item = decodeItem(data, offset, depth + 1);
                arr.push(item.value);
                offset = item.offset;
            }
            return { value: arr, offset };
        }
        case 5: {
            const map = new Map();
            for (let i = 0; i < arg; i++) {
                const k = decodeItem(data, offset, depth + 1);
                if (typeof k.value !== 'number' && typeof k.value !== 'string') {
                    throw new CborError('map keys must be integers or strings');
                }
                if (map.has(k.value))
                    throw new CborError('duplicate map key');
                const v = decodeItem(data, k.offset, depth + 1);
                map.set(k.value, v.value);
                offset = v.offset;
            }
            return { value: map, offset };
        }
        default:
            throw new CborError(`unsupported major type ${major}`);
    }
}
//# sourceMappingURL=cbor.js.map